<script lang="ts" setup>
const { size = "sm", closeButton = true } = defineProps<{
  size?: "sm" | "md" | "lg" | "xl";
  closeButton?: boolean;
}>();

const emit = defineEmits<{
  (e: "toggle-fallback", isOpening: boolean): void;
}>();

const dialog = useTemplateRef("dialog");

const platformStore = usePlatformStore();
const { getStyleFromTheme } = useGetStyleFromTheme();
const platformStyles = computed(() =>
  getStyleFromTheme(platformStore.getPlatformConfiguration.styling, {
    overwriteTheme: "light",
  }),
);

const isOpen = defineModel<boolean>({ default: false });

const open = () => {
  const isDialogOpen = dialog.value?.open;

  if (!isDialogOpen) {
    if (!dialog.value?.showModal) {
      emit("toggle-fallback", true);

      return;
    }

    dialog.value.showModal();
  }

  isOpen.value = true;
};

const close = () => {
  const isDialogOpen = dialog.value?.open;
  if (isDialogOpen) {
    if (!dialog.value?.close) {
      emit("toggle-fallback", false);

      return;
    }

    dialog.value.close();
  }

  if (isOpen.value) {
    isOpen.value = false;
  }
};

watch(
  [isOpen, dialog],
  ([newIsOpen, newDialog]) => {
    if (!newDialog) return;

    if (newIsOpen) {
      nextTick(open);
    } else close();
  },
  { immediate: true },
);

const onDialogClicked = (event: MouseEvent) => {
  if (event.target === dialog.value) {
    isOpen.value = false;
  }
};

const onDialogEscaped = () => {
  isOpen.value = false;
};
</script>

<template>
  <dialog
    v-if="isOpen"
    id="dialog"
    ref="dialog"
    class="base base-dialog overflow-x-hidden"
    :style="platformStyles"
    :class="[`base-dialog__${size}`]"
    @click="onDialogClicked"
    @keydown.esc="onDialogEscaped"
  >
    <div>
      <button
        v-if="closeButton"
        class="base-dialog__close-button"
        data-cy="dialog-close-button"
        data-test="dialog-close-button"
        @click="close"
      >
        <BaseIcon
          name="close"
          :size="22"
        />
      </button>

      <slot />
    </div>
  </dialog>
</template>

<style lang="postcss" scoped>
.base-dialog {
  @apply rounded-none border-none;
  overscroll-behavior: contain;

  &::backdrop {
    overflow: auto;
    overscroll-behavior: contain;
    @apply bg-black bg-opacity-50;
  }

  &__close-button {
    @apply absolute top-6 right-5 cursor-pointer;
    @apply flex mt-1 z-50;
  }
}
</style>
